import * as React from 'react';
import { ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import isEqual from 'react-fast-compare';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';

type Props = {
    catalogId: number;
    className?: string;
    itemId?: number;
    registerToBidMessageId?: string;
};

const RegisterLink = ({ catalogId, className, itemId, registerToBidMessageId = 'register_for_auction' }: Props) => {
    const { isCatalogOnly } = useSelector((state) => getCatalog(state, catalogId), isEqual);

    const dispatch = useDispatch();

    const modalContext: ModalContext = {
        id: itemId || catalogId,
        type: itemId ? 'register for auction - item' : 'register for auction',
    };

    const { declined, pending, registered, suspended } = useSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const registerForCatalog = React.useCallback((args) => dispatch(submitRegisterForCatalog(args)), [dispatch]);
    const openRegisterModal = React.useCallback((args) => dispatch(openRegisterForAuctionModal(args)), [dispatch]);

    let registeredMessage;
    if (suspended) {
        registeredMessage = 'account_suspended';
    } else if (declined) {
        registeredMessage = 'registration_declined';
    } else if (isCatalogOnly) {
        registeredMessage = 'request_submitted';
    } else {
        registeredMessage = pending ? 'registration_pending' : 'registration_approved';
    }

    if (registered) {
        return (
            <RegisteredSpan className={className}>
                <FormattedMessage id={registeredMessage} />
            </RegisteredSpan>
        );
    }

    if (isCatalogOnly) {
        return (
            <ShouldLogInContainer
                requireCompleteProfile
                requireLogin
                runFunction={registerForCatalog}
                runFunctionArgument={{ catalogId, itemId }}
            >
                <RegisterForAuctionSpan className={className}>
                    <FormattedMessage id="request_online_bidding" />
                </RegisterForAuctionSpan>
            </ShouldLogInContainer>
        );
    }

    // TODO: make sure this still works when logged in
    // Cannot confirm on local host because stripe requires HTTPS
    return (
        <ShouldLogInContainer
            modalContext={modalContext}
            requireCompleteProfile
            requireLogin
            runFunction={openRegisterModal}
            runFunctionArgument={{ catalogId, itemId }}
        >
            <RegisterForAuctionSpan className={className}>
                <FormattedMessage id={registerToBidMessageId} />
            </RegisterForAuctionSpan>
        </ShouldLogInContainer>
    );
};

export default React.memo<Props>(RegisterLink);

const RegisterForAuctionSpan = styled.span`
    font-size: 14px;
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    text-decoration: underline;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.blue200};

    :hover {
        color: ${({ theme }) => theme.colors.blue100};
    }
`;

const RegisteredSpan = styled.span`
    display: inline-block;
    text-decoration: none !important;
    user-select: none !important;
    color: ${({ theme }) => theme.colors.black} !important;

    @media print {
        display: none;
    }
`;
